/**
 * Shared types + helpers for the HubCreatorSection component family.
 */

import React from "react";
import { FaTwitch, FaYoutube, FaPlay } from "react-icons/fa";
import { SiKick } from "react-icons/si";
import type { StreamingPlatform } from "@devour/client";
import type { FeaturedStreamResponse, SocialPlatform, SurfaceThemeColors } from "@/hooks/hub/useGetHub";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

/*
 * Tab union for CreatorSection. CHALLENGES is conditional in the nav
 * (suppressed when `hasChallenges` is falsy); the union covers it
 * unconditionally so callers can target it as a default (`initialTab`)
 * without conditional typing — the nav render gates button visibility.
 *
 * No MERCH tab: creator storefronts are rendered as plain glyph-links in the
 * DropHero links row (next to socials), not a dedicated tab — a merch link is
 * just a link until real on-platform commerce (catalog + checkout + rewards)
 * ships. See drop-in-merch/BUILD_PLAN.md.
 */
export type ContentTab = "STREAM" | "CHALLENGES" | "CLIPS" | "SOCIAL";
export type SocialTab = "X" | "TIKTOK" | "INSTAGRAM" | "DISCORD";
export type StreamingSource = "TWITCH" | "KICK" | "YOUTUBE";

/* Re-exported from useGetHub for ergonomic imports within the creator-section file family. */
export type { SurfaceThemeColors };

/*
 * Twitch's Helix API returns `vod-secure.twitch.tv/_404/404_processing_*.png`
 * when a stream or VOD just went live and the preview hasn't generated yet.
 * That URL 403s when fetched. The backend filters most paths (see
 * `isPlaceholderTwitchThumb` in StreamService) but defense-in-depth on the
 * client prevents stray placeholder URLs from being requested at all.
 */
export function isPlaceholderTwitchThumb(url: string | undefined | null): boolean {
    return !!url && (/404_processing/i).test(url);
}

/*
 * Shape carried from ClipsTab → HubCreatorSection → StreamPanel when a user
 * clicks a clip to promote it into the main player. Carries the minimal
 * identifiers each platform's inline embed needs.
 */
export interface SelectedClip {
    id: string;
    source: StreamingSource | "TIKTOK";
    url: string;
    title: string;
    thumbnailUrl: string;
    /* For Kick VODs only — the m3u8 HLS source URL captured by the backend */
    hlsUrl?: string;
}

/*
 * Precedence = richest live-status data first. Twitch's Helix API returns the
 * most reliable live-flag, viewer count, and game category; YouTube live data
 * is the least signal-dense. Used when picking which platform to default the
 * embed to when the creator is live on more than one at the same time.
 */
/*
 * `NonNullable` because `FeaturedStreamResponse["platform"]` is now optional —
 * a roster member is not required to have a channel, and platform is a
 * property of a channel. This list is the precedence order among platforms
 * that DO exist, so `undefined` is not one of its members; without the
 * unwrapping every index by a value from this array widens to
 * `StreamingPlatform | undefined` and stops being a valid key.
 */
export const STREAM_PRECEDENCE: Array<NonNullable<FeaturedStreamResponse["platform"]>> = ["TWITCH", "KICK", "YOUTUBE"];

export function sanitize(id: string | undefined): string {
    if (!id) return "";
    return id.replace(/[^a-zA-Z0-9_-]/g, "");
}

/*
 * Twitch clip slugs additionally permit `.` (legacy dot-format slugs Twitch
 * issued before standardizing on alphanumeric+hyphen — those still resolve on
 * the embed endpoint). Channel ids and numeric VOD ids use the stricter
 * `sanitize` above; clip slugs use this variant.
 */
export function sanitizeClipSlug(s: string | undefined): string {
    return (s ?? "").replace(/[^a-zA-Z0-9._-]/g, "");
}

/*
 * Everything an embed URL can be built from, structurally — so a generated
 * `FeaturedStreamResponse`, a theater `StreamData` (with its `vod` ids
 * flattened) and an `InlinePlayerSource` all satisfy it without a translation
 * layer.
 *
 * `platform` is optional because it is a property of a CHANNEL and a roster
 * member is not required to have one; `MULTI` is a real member of the generated
 * union and lands on the empty-string default rather than on nothing.
 */
export interface StreamEmbedSource {
    platform?: StreamingPlatform;
    /* Live-channel identifiers */
    twitchChannelId?: string;
    kickChannelSlug?: string;
    youtubeChannelId?: string;
    youtubeLiveId?: string;
    /* Specific-media identifiers — more specific than the channel, so preferred */
    twitchVideoId?: string;
    twitchClipSlug?: string;
    youtubeVideoId?: string;
    /**
     * Kick clip id. Kick has no separate clip embed host — the clip plays on
     * the CHANNEL player via `?clip=<id>`, so this needs `kickChannelSlug`
     * alongside it rather than replacing it, unlike the Twitch clip branch.
     */
    kickClipId?: string;
}

export interface StreamEmbedOptions {
    /**
     * Start muted. **Defaults to whatever `autoplay` is**, because autoplaying
     * implies muted: every modern browser blocks UNMUTED autoplay without a
     * strong prior user-interaction signal, so `autoplay=true&muted=false` is a
     * request the browser refuses — the player renders paused and the viewer
     * sees nothing move. Muted autoplay plays; the viewer unmutes.
     *
     * An explicit value always wins, so a surface the user deliberately clicked
     * into (a theater, a clip tile) passes `muted: false` and gets sound — that
     * path follows a real interaction and is not subject to autoplay policy.
     */
    muted?: boolean;
    /**
     * Default true — the historical behaviour of every builder this replaced.
     *
     * StreamInlinePlayer opts OUT: Twitch's player runs a visibility check
     * before attempting autoplay and refuses (even muted) when the iframe
     * hasn't been painted at check time, logging a noisy warning while still
     * not autoplaying. Surfaces that mount hidden pass false.
     */
    autoplay?: boolean;
    /**
     * Append `enablejsapi=1` + `origin` to a YouTube embed so the YT IFrame
     * Player API can attach (onStateChange + isMuted) for the accessible
     * watch-time model. Only StreamInlinePlayer needs it; it is inert chrome
     * for everyone else, so it stays opt-in rather than always-on.
     */
    youtubeApi?: boolean;
}

/**
 * THE embed-URL builder. Every surface that puts a stream in an iframe goes
 * through here.
 *
 * There were seven of these. They drifted: two YouTube copies used a channel id
 * as a video id (the "Playback ID" error overlay), and the copies that grew VOD
 * or clip support grew it privately. Where a caller genuinely needs more, this
 * function grows an option — it does not get forked again.
 *
 * Precedence is most-specific-first: clip → VOD → live channel. A clip slug is
 * NOT a VOD id and has its own embed endpoint; without that branch a clip
 * selection falls through to the channel iframe and plays the live stream.
 *
 * Returns "" when there is no embeddable identifier, so callers can filter.
 */
export function getStreamEmbedUrl(stream: StreamEmbedSource, opts: StreamEmbedOptions = {}): string {
    const host = typeof window !== "undefined" ? window.location.hostname : "devourplay.gg";
    const autoplay = opts.autoplay ?? true;
    const muted = opts.muted ?? autoplay;

    switch (stream.platform) {
        case "TWITCH": {
            /*
             * Twitch embed requires ?parent= to match the embedding domain
             * exactly. Wrong value = blank player with a "security error".
             */
            const tail = `&parent=${host}&autoplay=${autoplay}&muted=${muted}`;
            const clip = sanitizeClipSlug(stream.twitchClipSlug);
            if (clip) return `https://clips.twitch.tv/embed?clip=${clip}${tail}`;
            const video = sanitize(stream.twitchVideoId);
            if (video) return `https://player.twitch.tv/?video=v${video}${tail}`;
            const channel = sanitize(stream.twitchChannelId);
            return channel ? `https://player.twitch.tv/?channel=${channel}${tail}` : "";
        }
        case "YOUTUBE": {
            /*
             * Two distinct embed shapes: `embed/<videoId>` for a known video or
             * live broadcast, `embed/live_stream?channel=<id>` for a channel's
             * CURRENT broadcast (YouTube resolves it at load time). Passing a
             * channel id where a video id belongs is what produced the
             * "Playback ID: ... Learn More" error overlay.
             */
            const mute = muted ? "&mute=1" : "";
            const api = opts.youtubeApi
                ? `&enablejsapi=1${typeof window !== "undefined" ? `&origin=${window.location.origin}` : ""}`
                : "";
            const tail = `?autoplay=${autoplay ? 1 : 0}${mute}${api}`;
            const videoId = sanitize(stream.youtubeVideoId || stream.youtubeLiveId);
            if (videoId) return `https://www.youtube.com/embed/${videoId}${tail}`;
            const channelId = sanitize(stream.youtubeChannelId);
            return channelId ? `https://www.youtube.com/embed/live_stream?channel=${channelId}&autoplay=${autoplay ? 1 : 0}${mute}${api}` : "";
        }
        case "KICK": {
            /*
             * Kick has no public VOD iframe — a past broadcast falls through to
             * the live-channel player, which renders Kick's own offline screen.
             * StreamInlinePlayer routes Kick VODs to hls.js instead.
             */
            const slug = sanitize(stream.kickChannelSlug);
            if (!slug) return "";
            /*
             * A clip rides the channel player rather than a separate host, so
             * `?clip=` is a parameter here instead of its own branch — the
             * opposite of Twitch, where a clip has a different embed endpoint.
             */
            const clipId = sanitizeClipSlug(stream.kickClipId);
            /* Kick honours ?autoplay=true&muted=true on its embed player. */
            const params = [
                clipId && `clip=${clipId}`,
                autoplay && `autoplay=true&muted=${muted}`,
            ].filter(Boolean).join("&");
            return params
                ? `https://player.kick.com/${slug}?${params}`
                : `https://player.kick.com/${slug}`;
        }
        default:
            return "";
    }
}

/**
 * THE external canonical URL for a stream — the escape hatch when the embedded
 * player is blocked by upstream content classification (Twitch's "Failed to
 * determine content classification" gate, age-gates, geofences), and the
 * "watch on <platform>" link everywhere else.
 *
 * There were four of these too. Same precedence as the embed builder:
 * clip → VOD → live channel. Returns undefined when there is no identifier to
 * link to; callers rendering a bare href supply their own fallback.
 */
export function getStreamExternalUrl(s: StreamEmbedSource): string | undefined {
    switch (s.platform) {
        case "TWITCH": {
            const clip = sanitizeClipSlug(s.twitchClipSlug);
            if (clip) return `https://clips.twitch.tv/${clip}`;
            const video = sanitize(s.twitchVideoId);
            if (video) return `https://www.twitch.tv/videos/${video}`;
            const channel = sanitize(s.twitchChannelId);
            return channel ? `https://www.twitch.tv/${channel}` : undefined;
        }
        case "YOUTUBE": {
            const videoId = sanitize(s.youtubeVideoId || s.youtubeLiveId);
            if (videoId) return `https://www.youtube.com/watch?v=${videoId}`;
            const channelId = sanitize(s.youtubeChannelId);
            /* `/live` deep-links to the channel's CURRENT broadcast rather than its home tab. */
            return channelId ? `https://www.youtube.com/channel/${channelId}/live` : undefined;
        }
        case "KICK": {
            const slug = sanitize(s.kickChannelSlug);
            return slug ? `https://kick.com/${slug}` : undefined;
        }
        default:
            return undefined;
    }
}

/**
 * True only for http(s) URLs — the gate before any admin-authored URL becomes
 * an href or an iframe src. `streamUrl` and `channelUrl` have no scheme
 * validation on write, so without this a `javascript:`/`data:` URL would be
 * used verbatim.
 *
 * Accepts `undefined` so callers holding an optional field don't each write
 * their own null check — that is how this ended up existing twice.
 */
export function isHttpUrl(url: string | undefined): boolean {
    if (!url) return false;
    try {
        const {protocol} = new URL(url);
        return protocol === "http:" || protocol === "https:";
    } catch {
        return false;
    }
}

/**
 * Lowercase because it names a CSS modifier and picks an icon, not because it
 * is a second spelling of `StreamingPlatform`. "unknown" is a real outcome and
 * callers must decide what to do about it — see `parseStreamUrl`.
 */
export type StreamPlatform = "twitch" | "youtube" | "kick" | "unknown";

export interface ParsedStream {
    platform: StreamPlatform;
    embedUrl: string;
    channelName?: string;
    videoId?: string;
    /**
     * The identifiers the URL resolved to. `embedUrl` is these run through
     * `getStreamEmbedUrl` with default options; a caller that needs different
     * options rebuilds from this rather than re-deriving ids from the URL —
     * which is the duplication this whole module exists to stop. Empty for
     * `platform: "unknown"`, which has no identifiers to give.
     */
    source: StreamEmbedSource;
}

/**
 * THE raw-URL parser: turns an admin-authored channel/broadcast URL into a
 * platform + an embed URL built by `getStreamEmbedUrl`.
 *
 * Returns null for a non-http(s) scheme, and `platform: "unknown"` with the URL
 * passed through for a host it doesn't recognize. **A caller that puts the
 * result in an iframe MUST refuse "unknown"** — the pass-through is there so a
 * caller can still offer an external link, not so it can embed any host.
 *
 * Already-formed embed URLs (`player.twitch.tv/?channel=`, `youtube.com/embed/`)
 * are recognized as their platform rather than falling to "unknown", because
 * stored block config contains them and treating them as unrecognized would
 * blank a player that renders today.
 */
export function parseStreamUrl(url: string): ParsedStream | null {
    if (!url) return null;
    try {
        const urlObj = new URL(url);
        /* Reject non-http(s) before any URL reaches an iframe src or an href. */
        if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") return null;
        const hostname = urlObj.hostname.toLowerCase();
        const pathParts = urlObj.pathname.split("/").filter(Boolean);

        if (hostname.includes("twitch.tv")) {
            /* Already an embed: player.twitch.tv/?channel=X or ?video=vX */
            const embeddedChannel = urlObj.searchParams.get("channel");
            const embeddedVideo = urlObj.searchParams.get("video");
            if (embeddedChannel || embeddedVideo) {
                const videoId = embeddedVideo?.replace(/^v/, "");
                const source: StreamEmbedSource = {
                    platform: "TWITCH",
                    twitchChannelId: embeddedChannel ?? undefined,
                    twitchVideoId: videoId,
                };
                return {
                    platform: "twitch",
                    embedUrl: getStreamEmbedUrl(source),
                    channelName: embeddedChannel ?? undefined,
                    videoId,
                    source,
                };
            }
            /*
             * clips.twitch.tv/<slug>, twitch.tv/<channel>/clip/<slug>, and the
             * already-formed clips.twitch.tv/embed?clip=<slug> — the last one
             * matters because reading its path would take "embed" as the slug.
             */
            const clipSlug = hostname.includes("clips.twitch.tv")
                ? pathParts[0] === "embed" ? urlObj.searchParams.get("clip") ?? undefined : pathParts[0]
                : pathParts[1] === "clip" ? pathParts[2] : undefined;
            if (clipSlug) {
                const source: StreamEmbedSource = {platform: "TWITCH", twitchClipSlug: clipSlug};
                return {platform: "twitch", embedUrl: getStreamEmbedUrl(source), source};
            }
            if (pathParts[0] === "videos" && pathParts[1]) {
                const source: StreamEmbedSource = {platform: "TWITCH", twitchVideoId: pathParts[1]};
                return {platform: "twitch", embedUrl: getStreamEmbedUrl(source), videoId: pathParts[1], source};
            }
            if (pathParts.length > 0) {
                const channelName = pathParts[0];
                const source: StreamEmbedSource = {platform: "TWITCH", twitchChannelId: channelName};
                return {platform: "twitch", embedUrl: getStreamEmbedUrl(source), channelName, source};
            }
        }

        /*
         * Kick. Without this branch a Kick official broadcast parsed as
         * "unknown" and every caller that gates on "unknown" showed no stream
         * at all.
         */
        if (hostname.includes("kick.com")) {
            /* player.kick.com/<slug> is already an embed; kick.com/<slug> is the channel. */
            const slug = pathParts[0];
            if (slug) {
                const source: StreamEmbedSource = {platform: "KICK", kickChannelSlug: slug};
                return {platform: "kick", embedUrl: getStreamEmbedUrl(source), channelName: slug, source};
            }
        }

        if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
            let videoId: string | null = null;
            let channelId: string | null = null;

            if (hostname.includes("youtu.be")) {
                /* Short URL: youtu.be/VIDEO_ID */
                videoId = pathParts[0] ?? null;
            } else if (urlObj.pathname.includes("/watch")) {
                videoId = urlObj.searchParams.get("v");
            } else if (urlObj.pathname.includes("/live/")) {
                videoId = urlObj.pathname.split("/live/")[1]?.split("?")[0] ?? null;
            } else if (urlObj.pathname.includes("/embed/live_stream")) {
                /* Already the channel-live embed shape. */
                channelId = urlObj.searchParams.get("channel");
            } else if (urlObj.pathname.includes("/embed/")) {
                videoId = urlObj.pathname.split("/embed/")[1]?.split("?")[0] ?? null;
            } else if (pathParts[0] === "channel" && pathParts[1]) {
                channelId = pathParts[1];
            }

            if (videoId) {
                const source: StreamEmbedSource = {platform: "YOUTUBE", youtubeVideoId: videoId};
                return {platform: "youtube", embedUrl: getStreamEmbedUrl(source), videoId, source};
            }
            if (channelId) {
                const source: StreamEmbedSource = {platform: "YOUTUBE", youtubeChannelId: channelId};
                return {platform: "youtube", embedUrl: getStreamEmbedUrl(source), channelName: channelId, source};
            }
        }

        return {platform: "unknown", embedUrl: url, source: {}};
    } catch {
        return null;
    }
}

/*
 * Minimal shape the chat-embed builder needs. Structural, so a
 * `FeaturedStreamResponse`, a theater `StreamData` and ChatPanel's own props
 * all satisfy it without a translation layer.
 */
export interface ChatEmbedSource {
    platform?: StreamingPlatform;
    twitchChannelId?: string;
    kickChannelSlug?: string;
    youtubeLiveId?: string;
}

/**
 * The platform-native chat iframe URL, or null when there is nothing to embed.
 *
 * Kick's chatroom embed URL is `kick.com/popout/<slug>/chat`.
 * `kick.com/<slug>/chatroom` is an API path that returns JSON — the older
 * StreamTheater pattern pointed there and was broken. That fix lived only in
 * ChatPanel and never reached the two copies, so this is now the one builder.
 *
 * Returns null rather than a URL with an empty identifier: an iframe pointed at
 * `twitch.tv/embed//chat` loads a garbage page instead of rendering nothing.
 */
export function getChatEmbedUrl(stream: ChatEmbedSource): string | null {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    switch (stream.platform) {
        case "TWITCH": {
            const ch = sanitize(stream.twitchChannelId);
            return ch ? `https://www.twitch.tv/embed/${ch}/chat?parent=${host}&darkpopout` : null;
        }
        case "KICK": {
            const slug = sanitize(stream.kickChannelSlug);
            return slug ? `https://kick.com/popout/${slug}/chat` : null;
        }
        case "YOUTUBE": {
            const vid = sanitize(stream.youtubeLiveId);
            return vid ? `https://www.youtube.com/live_chat?v=${vid}&embed_domain=${host}` : null;
        }
        default:
            return null;
    }
}

export function formatDuration(seconds?: number): string {
    if (!seconds || seconds <= 0) return "";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatViews(views?: number): string {
    return formatCompactNumber(views, {empty: ""});
}

export function timeAgo(iso?: string | number): string {
    if (!iso) return "";
    const then = typeof iso === "number" ? iso : new Date(iso).getTime();
    const diffSec = Math.floor((Date.now() - then) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    if (diffSec < 2592000) return `${Math.floor(diffSec / 604800)}w ago`;
    return `${Math.floor(diffSec / 2592000)}mo ago`;
}

export function platformIcon(platform: StreamingSource): React.ReactNode {
    switch (platform) {
        case "TWITCH": return <FaTwitch />;
        case "YOUTUBE": return <FaYoutube />;
        case "KICK": return <SiKick />;
        default: return <FaPlay />;
    }
}

/**
 * Title-case a platform name for display.
 *
 * Accepts `SocialPlatform` too: DropHero labels its social deeplinks with this
 * and was reaching it through `as StreamingSource`, which claimed TWITTER and
 * TIKTOK were streaming sources to make a title-casing call type-check. Nothing
 * in here is streaming-specific, so the signature says so instead.
 */
export function platformLabel(platform: StreamingSource | SocialPlatform): string {
    return platform.charAt(0) + platform.slice(1).toLowerCase();
}

/**
 * The API's platform, narrowed to the three this UI vocabulary covers.
 *
 * `StreamingSource` is NOT a copy of `StreamingPlatform` — it is the set the
 * creator-section widgets have an icon, a label, a chat embed and a player for.
 * `StreamingPlatform` has a fourth member, `MULTI`, and is optional besides,
 * because platform is a property of a CHANNEL and a roster member need not
 * have one.
 *
 * Those two facts were being reconciled with `as StreamingSource` at eight call
 * sites, which is not a conversion — it is an instruction to the compiler to
 * stop checking. `platformLabel` immediately calls `.charAt(0)`, so a live
 * stream with no platform threw `TypeError: Cannot read properties of
 * undefined` in the player bar, and a MULTI stream was labelled and routed as
 * whichever of the three it textually was not.
 *
 * Returns `undefined` for both cases, so the caller has to say what it wants to
 * do about them — every current one hides the platform-specific affordance,
 * which is the honest answer when there is no single platform to name.
 */
export function asStreamingSource(platform?: StreamingPlatform): StreamingSource | undefined {
    return platform === "TWITCH" || platform === "KICK" || platform === "YOUTUBE"
        ? platform
        : undefined;
}
